import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button
} from "reactstrap";

import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { RiMusic2Line } from "react-icons/ri";
import KstTrackLinks from "../../components/kst/KstTrackLinks";

function toSlug(name) {
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
}

export default function AuthorSongsPage() {

    const params = useParams();
    // Handles both /authors/:segment (GUID only) and /authors/:authorId/:slug
    const authorId = params.authorId ?? params.segment;
    const slug = params.slug;

    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        Api.get(`/api/authors/${authorId}/seo`)
            .then(res => setData(res.data))
            .catch(err => console.error("Failed to load author songs", err));
    }, [authorId]);

    // Redirect GUID-only URLs to canonical slug form once name is known
    useEffect(() => {
        if (data && !slug) {
            navigate(`/authors/${authorId}/${toSlug(data.name)}`, { replace: true });
        }
    }, [data, slug, authorId, navigate]);

    const canonicalSlug = data ? toSlug(data.name) : slug;
    const canonical = canonicalSlug
        ? `/authors/${authorId}/${canonicalSlug}`
        : `/authors/${authorId}`;

    const seo = {
        title: `Worship Songs by ${data?.name ?? "this Author"}`,
        description: data?.metaDescription ?? `Discover worship songs written by this author.`,
        canonical
    };

    return (
        <>
            <SeoHelper
                uri={seo.canonical}
                title={seo.title}
                description={seo.description}
            />

            {!data ? (
                <div className="text-center mt-5">Loading...</div>
            ) : (() => {

                const sortedSongs = [...data.songs].sort((a, b) =>
                    a.title.localeCompare(b.title)
                );

                const visibleSongs = sortedSongs;

                return (
                    <section className="section section-lg">

                        <Container>

                            <Row className="justify-content-center">

                                <Col lg="9" xl="8">

                                    <h1 className="display-4 text-center text-primary mb-3">
                                        {data.heading || `Worship Songs by ${data.name}`}
                                    </h1>

                                    <p className="lead text-center text-muted mb-4">
                                        Discover worship songs written by {data.name}.
                                    </p>

                                    <Card className="mb-4">
                                        <CardBody>

                                            <h2 className="h4 mb-3">
                                                <RiMusic2Line size={22} style={{ marginRight: "8px" }} />
                                                Songs by {data.name}
                                            </h2>

                                            {data.songs?.length > 0 ? (
                                                <ul className="twb-grid">
                                                    {visibleSongs.map(song => (
                                                        <li key={song.id}>
                                                            <div className="twb-next-step">
                                                                <div className="twb-next-step-icon">
                                                                    <RiMusic2Line size={18}/>
                                                                </div>

                                                                <div className="twb-next-step-text">
                                                                    <div style={{fontWeight: "500"}}>
                                                                        {song.title}
                                                                    </div>
                                                                    <KstTrackLinks kstTracks={song.kstTracks} seo />

                                                                    {song.alternativeTitle && (
                                                                        <div className="small text-muted">
                                                                            {song.alternativeTitle}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">
                                                    There are currently no songs for this author.
                                                </p>
                                            )}

                                            {!sessionStorage.getItem("customer") && (
                                                <div className="alert alert-light border d-flex justify-content-between align-items-center mt-4">
                                                    <span>
                                                        Planning a service? Create an account to save your song-flow and come back to it later.
                                                    </span>
                                                    <a href="/register/choose-your-plan" className="btn btn-sm btn-primary ml-3">
                                                        Create account →
                                                    </a>
                                                </div>
                                            )}

                                        </CardBody>
                                    </Card>

                                    <Card className="mb-4">
                                        <CardBody className="text-center">

                                            <h3 className="h5 mb-3">
                                                Plan a Worship Service with Songs by {data.name}
                                            </h3>

                                            <Button color="primary" href="/themes/choose-themes">
                                                Start Planning a Song-Flow
                                            </Button>

                                        </CardBody>
                                    </Card>

                                </Col>

                            </Row>

                        </Container>

                    </section>
                );

            })()}
        </>
    );
}
